import { Directive, effect, ElementRef, inject, input, output } from '@angular/core';

/**
 * Célula editável da tabela: um `contenteditable` só de texto que o Angular não reescreve
 * enquanto o usuário digita.
 *
 * Ligar `[textContent]` direto jogaria o cursor para o começo a cada tecla. Aqui o texto
 * do modelo só entra no DOM quando a célula NÃO está em foco (carga, eco do servidor,
 * linha removida acima); o que o usuário digita sai por `cellTextChange`. Colar e Enter
 * nunca criam quebra de linha: a célula é uma linha só, como a API grava.
 */
@Directive({
    selector: '[tableCellText]',
    // `contenteditable` fica no template: vira `false` com o documento bloqueado.
    host: {
        spellcheck: 'true',
        '(input)': 'onInput()',
        '(paste)': 'onPaste($event)',
    },
})
export class TableCellTextDirective {
    public tableCellText = input<string>('');
    public cellTextChange = output<string>();

    private _el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    constructor() {
        effect(() => {
            const text = this.tableCellText();
            if (document.activeElement === this._el) return;
            if ((this._el.textContent ?? '') !== text) this._el.textContent = text;
        });
    }

    public onInput(): void {
        // Apagar tudo às vezes deixa um <br>: sem ele o :empty volta a mostrar o placeholder.
        if (!this._el.textContent) this._el.innerHTML = '';
        this.cellTextChange.emit((this._el.textContent ?? '').replace(/[\r\n]+/g, ' '));
    }

    public onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = (event.clipboardData?.getData('text/plain') ?? '').replace(
            /\s*[\r\n]+\s*/g,
            ' '
        );
        document.execCommand('insertText', false, text);
    }
}
